export const AUSTRALIAN_STATES = [
	{ value: 'ACT', label: 'Australian Capital Territory' },
	{ value: 'NSW', label: 'New South Wales' },
	{ value: 'NT', label: 'Northern Territory' },
	{ value: 'QLD', label: 'Queensland' },
	{ value: 'SA', label: 'South Australia' },
	{ value: 'TAS', label: 'Tasmania' },
	{ value: 'VIC', label: 'Victoria' },
	{ value: 'WA', label: 'Western Australia' },
] as const;

type AustralianDetailsValue = string | number | null | undefined;

export type AustralianAddressAndAbn = {
	streetAddress1: AustralianDetailsValue;
	streetAddress2: AustralianDetailsValue;
	addressCity: AustralianDetailsValue;
	addressState: AustralianDetailsValue;
	addressPostcode: AustralianDetailsValue;
	abn: AustralianDetailsValue;
};

export type AustralianAddressAndAbnErrors = Partial<Record<keyof AustralianAddressAndAbn, string>>;

export function isAustralianState(value: string) {
	return AUSTRALIAN_STATES.some((state) => state.value === value);
}

export function normaliseAustralianPhone(value: AustralianDetailsValue) {
	const digits = textValue(value).replace(/\D/g, '');
	return digits.length === 11 && digits.startsWith('61') ? `0${digits.slice(2)}` : digits;
}

export function isValidAustralianPhone(value: AustralianDetailsValue) {
	const text = textValue(value);
	return !/[^0-9\s()+.-]/.test(text) && /^0\d{9}$/.test(normaliseAustralianPhone(text));
}

export function normaliseAbn(value: AustralianDetailsValue) {
	return textValue(value).replace(/\D/g, '');
}

export function isValidAbn(value: AustralianDetailsValue) {
	const text = textValue(value);
	if (/[^0-9\s]/.test(text)) return false;
	const digits = normaliseAbn(text);
	if (!/^[1-9]\d{10}$/.test(digits)) return false;
	const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
	const sum = digits.split('').reduce((total, digit, index) => {
		return total + (Number(digit) - (index === 0 ? 1 : 0)) * (weights[index] ?? 0);
	}, 0);
	return sum % 89 === 0;
}

export function validateAustralianAddressAndAbn(
	values: AustralianAddressAndAbn,
	requireAddress = true,
): AustralianAddressAndAbnErrors {
	const errors: AustralianAddressAndAbnErrors = {};
	const address = {
		streetAddress1: textValue(values.streetAddress1),
		streetAddress2: textValue(values.streetAddress2),
		addressCity: textValue(values.addressCity),
		addressState: textValue(values.addressState),
		addressPostcode: textValue(values.addressPostcode),
	};
	const abn = textValue(values.abn);
	const hasAddress = [
		address.streetAddress1,
		address.streetAddress2,
		address.addressCity,
		address.addressState,
		address.addressPostcode,
	].some(Boolean);

	if (requireAddress || hasAddress) {
		if (!address.streetAddress1) errors.streetAddress1 = 'Enter street address 1.';
		if (!address.addressCity) errors.addressCity = 'Enter the suburb or city.';
		if (!isAustralianState(address.addressState)) {
			errors.addressState = 'Select an Australian state or territory.';
		}
		if (!/^\d{4}$/.test(address.addressPostcode)) {
			errors.addressPostcode = 'Enter a four-digit postcode.';
		}
	}

	if (abn && !isValidAbn(abn)) errors.abn = 'Enter a valid 11-digit ABN.';
	return errors;
}

function textValue(value: AustralianDetailsValue) {
	return String(value ?? '').trim();
}

export function formatAbn(value: AustralianDetailsValue) {
	const text = textValue(value);
	const digits = normaliseAbn(text);
	return digits.length === 11
		? `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`
		: text;
}
